import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, KeyRound, LockKeyhole, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';

export default function AdminLogin() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [form, setForm] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = 'RESQLY Admin';
  }, []);

  const submit = async (event) => {
    event?.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post('/admin/login', {
        username: form.username || undefined,
        password: form.password,
      });
      login(data.token, 'admin');
      toast.success(`Welcome ${data.admin?.display_name || 'admin'}`);
      navigate(data.admin?.must_change_password ? '/admin/security' : '/admin/dashboard', { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid admin credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-auth">
      <div className="admin-auth-noise" />
      <motion.div
        className="admin-auth-card"
        initial={{ opacity: 0, y: 26, scale: 0.98, filter: 'blur(12px)' }}
        animate={{ opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }}
        transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="admin-auth-topline">
          <div className="admin-auth-mark"><ShieldCheck size={25} /></div>
          <div className="admin-auth-lock"><LockKeyhole size={14} /> Restricted system</div>
        </div>
        <div className="admin-eyebrow"><KeyRound size={13} /> RESQLY operations</div>
        <h1>Admin Console</h1>
        <p className="admin-auth-copy">A secure operations workspace for RESQLY. Only authorised team members can enter.</p>

        <form className="admin-auth-form" onSubmit={submit}>
          <label>
            <span>Admin ID</span>
            <input autoFocus autoComplete="username" placeholder="owner or your team ID" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
          </label>
          <label>
            <span>Password</span>
            <input type="password" autoComplete="current-password" placeholder="••••••••••••" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </label>
          <button className="admin-lux-button wide" disabled={loading}>
            {loading ? 'Verifying access…' : 'Enter operations console'}
            <ArrowRight size={17} />
          </button>
        </form>

        <div className="admin-auth-foot">
          <Link to="/admin/recover">Team member password recovery</Link>
          <span>·</span>
          <Link to="/">Return to RESQLY</Link>
        </div>

        <div className="admin-auth-security-note">
          <span className="admin-live-dot" />
          <div><strong>Session protected</strong><small>Admin sessions are scoped to this browser and expire after 8 hours.</small></div>
        </div>
      </motion.div>
    </div>
  );
}
